"use client"

import * as React from "react"
import * as TooltipPrimitive from "@radix-ui/react-tooltip"

import { cn } from "@workspace/ui/lib/utils"

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  )
}

const TapTooltipContext = React.createContext<{
  open: boolean
  setOpen: (open: boolean) => void
} | null>(null)

function Tooltip({
  tapToOpen = false,
  open: controlledOpen,
  defaultOpen = false,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root> & { tapToOpen?: boolean }) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen)
  const open = controlledOpen ?? uncontrolledOpen
  const setOpen = React.useCallback((nextOpen: boolean) => {
    if (controlledOpen === undefined) setUncontrolledOpen(nextOpen)
    onOpenChange?.(nextOpen)
  }, [controlledOpen, onOpenChange])
  const tapContext = React.useMemo(
    () => tapToOpen ? { open, setOpen } : null,
    [tapToOpen, open, setOpen],
  )

  return (
    <TooltipProvider>
      <TapTooltipContext.Provider value={tapContext}>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          open={open}
          onOpenChange={setOpen}
          {...props}
        />
      </TapTooltipContext.Provider>
    </TooltipProvider>
  )
}

function TooltipTrigger({
  onPointerDown,
  onPointerCancel,
  onKeyDown,
  onClick,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  const tapContext = React.useContext(TapTooltipContext)
  const openBeforePress = React.useRef<boolean | null>(null)

  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      aria-expanded={tapContext?.open}
      {...props}
      onPointerDown={(event) => {
        onPointerDown?.(event)
        // Radix closes on pointerdown. Remember the state before that close so
        // the subsequent click can toggle, including a second tap to dismiss.
        openBeforePress.current = tapContext?.open ?? null
      }}
      onPointerCancel={(event) => {
        openBeforePress.current = null
        onPointerCancel?.(event)
      }}
      onKeyDown={(event) => {
        openBeforePress.current = null
        onKeyDown?.(event)
      }}
      onClick={(event) => {
        onClick?.(event)
        const wasOpen = openBeforePress.current ?? tapContext?.open
        openBeforePress.current = null
        if (!tapContext || event.defaultPrevented) return
        // Opt in only for informational controls; navigation/action tooltips
        // keep their normal click behavior. Skip Radix's click-to-close handler.
        event.preventDefault()
        tapContext.setOpen(!wasOpen)
      }}
    />
  )
}

function TooltipContent({
  className,
  sideOffset = 4,
  children,
  collisionPadding = 8,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={cn(
          "z-50 w-fit max-w-xs origin-(--radix-tooltip-content-transform-origin) rounded-md border border-border bg-popover px-3 py-1.5 text-xs text-popover-foreground shadow-md whitespace-normal break-words animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-popover fill-popover" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  )
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
